import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import VoiceCallsLogged from 'components/molecules/voice-calls-logged';
import SmsActivityLogged from 'components/molecules/sms-activity-logged';
import ManualClinicalNotesLogged from 'components/molecules/manual-clinical-notes-logged';
import { ModuleType } from 'infrastructure/enums';
import ClinicalNotesLoggedTable from 'components/molecules/clinical-notes-logged';
import { useAppSelector } from 'store';
import SegmentedTabs from 'components/atoms/oneplatform/segmented-tabs';
import {
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  FileMarkdownOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useModuleWithLocalApcm } from 'infrastructure/providers/module-provider';
import ModuleTabs from 'components/molecules/module-tabs';
import { Flex } from 'antd';
import BaseButton from 'components/atoms/baseButton';

import s from './styles.module.scss';

import type { DateRange } from 'infrastructure/types';
import type { FC } from 'react';
import type { IBaseTab } from 'components/atoms/oneplatform/segmented-tabs';

interface ClinicalActivityAndInterventionProps {
  patientGuid: string;
}

enum ClinicalTabs {
  VoiceCalls = 0,
  SmsActivity = 1,
  ClinicalNotes = 2,
  ManualClinicalNotes = 3,
}

const CombinedClinicalActivityAndInterventions: FC<
  ClinicalActivityAndInterventionProps
> = ({ patientGuid }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<ClinicalTabs>(
    ClinicalTabs.VoiceCalls,
  );
  const [isExpanded, setIsExpanded] = useState(true);

  const { module: moduleType, changeModule: setModuleType } =
    useModuleWithLocalApcm(ModuleType.RPM);
  const dateRange = useAppSelector<DateRange>(
    (state) => state.ccmPatientDetails.dates,
  );

  const tabs: IBaseTab[] = [
    {
      key: 0,
      label: (
        <>
          <PhoneOutlined /> {t('labels.voiceCallsLogged')}
        </>
      ),
      children: (
        <VoiceCallsLogged
          patientGuid={patientGuid}
          module={moduleType}
          showTitle={false}
        />
      ),
    },
    {
      key: 1,
      label: (
        <>
          <MailOutlined /> {t('labels.smsActivityLogged')}
        </>
      ),
      children: (
        <SmsActivityLogged
          patientGuid={patientGuid}
          module={moduleType}
          showTitle={false}
        />
      ),
    },
    {
      key: 2,
      label: (
        <>
          <FileTextOutlined /> {t('labels.clinicalNotes')}
        </>
      ),
      children: (
        <ClinicalNotesLoggedTable
          patientGuid={patientGuid}
          module={moduleType}
          dateRange={dateRange}
          showTitle={false}
        />
      ),
    },
    {
      key: 3,
      label: (
        <>
          <FileMarkdownOutlined /> {t('labels.manualClinicNotesLogged')}
        </>
      ),
      children: (
        <ManualClinicalNotesLogged
          patientGuid={patientGuid}
          module={moduleType}
          showTitle={false}
        />
      ),
    },
  ];

  return (
    <Flex className={s.wrapper} id="clinical-activity">
      <div className={s['header-wrapper']}>
        <div className={s['header-left-wrapper']}>
          <BaseButton
            type="secondary"
            className={s['toggle-button']}
            icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          <h4 className={s.title} data-cy="clinical-activity-section">
            {t('labels.clinicalActivityAndClinicalInterventions')}
          </h4>
        </div>

        <ModuleTabs
          activeKey={moduleType}
          className={s.tabs}
          onChange={(tab) => {
            setModuleType(tab.key);
          }}
          showApcm={activeTab === ClinicalTabs.ClinicalNotes}
        />
      </div>
      {isExpanded && (
        <SegmentedTabs
          tabs={tabs}
          defaultIndex={activeTab}
          onChange={(_, index) => {
            setModuleType(ModuleType.RPM);
            setActiveTab(index);
          }}
          type="rounded"
          size="small"
          wrapperClassName={s['segmented-tabs-wrapper']}
        />
      )}
    </Flex>
  );
};
export default CombinedClinicalActivityAndInterventions;
